import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import {
  Container, Typography, Button, Box, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton, Alert, CircularProgress,
  List, ListItem, ListItemText, ListItemSecondaryAction,
  Chip
} from '@mui/material';
import { Add, Edit, Delete, Link as LinkIcon } from '@mui/icons-material';
import axios from 'axios';
import Cookies from 'js-cookie';

const AdminNewsManagementPage = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    link: ''
  });

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.get(`${API_BASE_URL}/news/list`);
      setNews(response.data.news || []);
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Failed to load news');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (index = null) => {
    if (index !== null) {
      // Editing existing news
      setEditingIndex(index);
      setFormData({
        title: news[index].title || '',
        content: news[index].content || '',
        link: news[index].link || ''
      });
    } else {
      // Creating new news
      setEditingIndex(null);
      setFormData({
        title: '',
        content: '',
        link: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingIndex(null);
    setFormData({ title: '', content: '', link: '' });
  };

  const handleSubmit = async () => {
    setError('');

    if (!formData.title || !formData.content) {
      setError('Title and content are required');
      return;
    }

    try {
      const token = Cookies.get('idToken');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      if (editingIndex !== null) {
        // Update existing
        await axios.put(
          `${API_BASE_URL}/news/update/${editingIndex}`,
          formData,
          config
        );
      } else {
        // Create new
        await axios.post(
          `${API_BASE_URL}/news/create`,
          formData,
          config
        );
      }

      handleCloseDialog();
      fetchNews();
    } catch (err) {
      console.error('Error saving news:', err);
      setError(err.response?.data?.error || 'Failed to save news');
    }
  };

  const handleDelete = async (index) => {
    if (!window.confirm('Are you sure you want to delete this news post?')) {
      return;
    }

    setError('');

    try {
      const token = Cookies.get('idToken');
      await axios.delete(
        `${API_BASE_URL}/news/delete/${index}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      fetchNews();
    } catch (err) {
      console.error('Error deleting news:', err);
      setError(err.response?.data?.error || 'Failed to delete news');
    }
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Loading news...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          📰 Manage News Posts
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add News
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" paragraph>
        Manage news posts that appear on the homepage. The 2 most recent posts will be displayed.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {news.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No news posts yet
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Click "Add News" to create your first post
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <List>
          {news.map((item, index) => (
            <Card key={index} sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="h6">
                        {item.title}
                      </Typography>
                      {index < 2 && (
                        <Chip
                          label="Shown on Homepage"
                          color="primary"
                          size="small"
                        />
                      )}
                    </Box>

                    <Typography variant="body2" color="text.secondary" paragraph>
                      {item.content}
                    </Typography>

                    {item.link && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <LinkIcon fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">
                          {item.link}
                        </Typography>
                      </Box>
                    )}

                    <Typography variant="caption" color="text.secondary">
                      Posted: {item.date || 'Unknown'} • Author: {item.author || 'Admin'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenDialog(index)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(index)}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </List>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingIndex !== null ? 'Edit News Post' : 'Add News Post'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Title"
              fullWidth
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />

            <TextField
              label="Content"
              fullWidth
              multiline
              rows={4}
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              required
              helperText="Brief description or excerpt"
            />

            <TextField
              label="Link (Optional)"
              fullWidth
              value={formData.link}
              onChange={(e) => setFormData({ ...formData, link: e.target.value })}
              placeholder="https://usc.edu.tt/news/..."
              helperText='Leave blank to link to "View All News"'
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingIndex !== null ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminNewsManagementPage;
